import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { Card, EmptyState, TabHeading } from "./StudioShared";
import { AreaChart, BarList } from "./charts";

// Normalise des formes de payload variées (AzuraCast-ish) en {labels, data}.
function toSeries(overview) {
  if (!overview) return { labels: [], data: [] };
  // Forme {labels, datasets:[{label,data}]}
  if (Array.isArray(overview.labels) && Array.isArray(overview.datasets)) {
    const ds = overview.datasets[0] || {};
    const raw = ds.data || [];
    // data peut être [{x,y}] ou [n].
    const data = raw.map((p) => (p && typeof p === "object" ? Number(p.y ?? p.value ?? 0) : Number(p) || 0));
    return { labels: overview.labels.map(String), data };
  }
  // Forme [{x/label, y/value}]
  if (Array.isArray(overview)) {
    return {
      labels: overview.map((p) => String(p.label ?? p.x ?? p.time ?? "")),
      data: overview.map((p) => Number(p.value ?? p.y ?? p.listeners ?? 0)),
    };
  }
  return { labels: [], data: [] };
}

// Normalise une liste "par pays" en [{label, value}].
function toCountryList(byCountry) {
  if (!byCountry) return [];
  if (Array.isArray(byCountry)) {
    return byCountry.map((c) => ({
      label: c.country || c.name || c.label || c.code || "—",
      value: Number(c.listeners ?? c.value ?? c.count ?? 0),
    }));
  }
  if (Array.isArray(byCountry.labels) && Array.isArray(byCountry.datasets)) {
    const ds = byCountry.datasets[0] || {};
    return byCountry.labels.map((lb, i) => ({ label: String(lb), value: Number(ds.data?.[i] ?? 0) }));
  }
  return [];
}

function toSongList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((s) => ({
    title: s.title || s.song?.title || s.text || s.song?.text || "—",
    artist: s.artist || s.song?.artist || "",
    value: Number(s.plays ?? s.play_count ?? s.deltaPositive ?? s.value ?? 0),
  }));
}

function SongList({ items, t }) {
  if (items.length === 0) return <p className="text-sm text-gray-500">{t("stats.notEnoughData")}</p>;
  return (
    <ul className="divide-y divide-gray-100">
      {items.slice(0, 8).map((s, i) => (
        <li key={i} className="flex items-center gap-3 py-2.5">
          <span className="w-6 text-xs text-gray-400">{i + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{s.title}</div>
            {s.artist && <div className="text-xs text-gray-500 truncate">{s.artist}</div>}
          </div>
          <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{s.value} {t("stats.plays")}</span>
        </li>
      ))}
    </ul>
  );
}

export default function StatsTab({ id }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const get = (path) => api.get(`/api/radios/${id}/${path}`).then((r) => r.data).catch(() => null);
    Promise.all([
      get("stats/listeners"),
      get("stats/overview"),
      get("stats/by-country"),
      get("stats/best-worst"),
      get("history"),
    ]).then(([listeners, overview, byCountry, bestWorst, history]) => {
      if (mounted) setData({ listeners, overview, byCountry, bestWorst, history });
    });
    return () => { mounted = false; };
  }, [id]);

  if (!data) return <p className="text-gray-500">{t("studio.loading")}</p>;

  const current =
    (typeof data.listeners === "number" ? data.listeners : null) ??
    data.listeners?.current ??
    data.listeners?.listeners?.current ??
    data.listeners?.unique ??
    0;

  const series = toSeries(data.overview);
  const countries = toCountryList(data.byCountry);
  const best = toSongList(data.bestWorst?.best ?? data.bestWorst?.top ?? (Array.isArray(data.bestWorst) ? data.bestWorst : []));
  const worst = toSongList(data.bestWorst?.worst ?? data.bestWorst?.bottom ?? []);
  const history = Array.isArray(data.history) ? data.history : (data.history?.rows || []);

  return (
    <div className="space-y-6">
      <TabHeading title={t("stats.title")} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* KPI auditeurs actuels */}
        <Card className="flex flex-col justify-center">
          <p className="text-sm text-gray-500">{t("stats.currentListeners")}</p>
          <p className="text-4xl font-bold text-gray-900 mt-1">{current}</p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs text-green-600">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> live
          </span>
        </Card>

        {/* Auditeurs dans le temps */}
        <Card className="lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-3">{t("stats.listenersOverTime")}</h3>
          {series.data.length > 1 ? (
            <AreaChart data={series.data} labels={series.labels} />
          ) : (
            <EmptyState icon="📈" title={t("stats.notEnoughData")} />
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">{t("stats.byCountry")}</h3>
          {countries.length > 0 ? (
            <BarList items={countries.slice(0, 8)} />
          ) : (
            <p className="text-sm text-gray-500">{t("stats.notEnoughData")}</p>
          )}
        </Card>

        <div className="grid grid-cols-1 gap-6">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">{t("stats.bestSongs")}</h3>
            <SongList items={best} t={t} />
          </Card>
          {worst.length > 0 && (
            <Card>
              <h3 className="font-semibold text-gray-900 mb-3">{t("stats.worstSongs")}</h3>
              <SongList items={worst} t={t} />
            </Card>
          )}
        </div>
      </div>

      {/* Historique récent */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-3">{t("stats.recentHistory")}</h3>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">{t("stats.noHistory")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pe-3 font-medium">{t("stats.song")}</th>
                  <th className="py-2 pe-3 font-medium">{t("stats.listeners")}</th>
                  <th className="py-2 font-medium">{t("stats.time")}</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 15).map((h, i) => {
                  const song = h.song || h.now_playing?.song || {};
                  const text = song.text || [song.artist, song.title].filter(Boolean).join(" — ") || h.title || "—";
                  const ts = h.played_at || h.playedAt || h.timestamp;
                  const when = ts ? new Date(typeof ts === "number" ? ts * 1000 : ts).toLocaleString() : "";
                  return (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-2 pe-3 text-gray-900 truncate max-w-[16rem]">{text}</td>
                      <td className="py-2 pe-3 text-gray-500">{h.listeners ?? h.unique_listeners ?? "—"}</td>
                      <td className="py-2 text-gray-500 whitespace-nowrap">{when}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
